// ============================================================================
// LIMAÇON CURVE PARAMETERS
// ============================================================================
// A limaçon (limacon) is a mathematical curve defined in polar coordinates
// by the equation: r = a + b*cos(θ)
//
// When b > a, the curve has an inner loop (which we use for figure skating patterns)
// The parameters below are calibrated to match a circle diameter of 140 (radius 70)

const a = 38; // Base radius - controls the overall width of the curve (2a = 76)
const b = 88; // Loop amplitude - controls the size of the inner loop (b > a creates the loop)

/**
 * ============================================================================
 * CORE PATH GENERATOR
 * ============================================================================
 * Generates SVG path data and total path length for a limaçon curve.
 * This is the main workhorse function used by all specific limacon generators.
 *
 * The function:
 * 1. Iterates through angles from startTheta to endTheta
 * 2. Converts polar coordinates (r, theta) to Cartesian (x, y)
 * 3. Calculates the total path length by summing distances between points
 * 4. Returns both the SVG path string and the calculated length
 *
 * @param {number} centerX - X coordinate of the curve's center point
 * @param {number} centerY - Y coordinate of the curve's center point
 * @param {number} startTheta - Starting angle in radians (0 to 2π)
 * @param {number} endTheta - Ending angle in radians (0 to 2π)
 * @param {number} step - Angular increment (positive for forward, negative for reverse)
 * @returns {{pathData: string, pathLength: number}} Object containing SVG path and length
 */
function generateLimaconPath(centerX, centerY, startTheta, endTheta, step) {
  let points = []; // Array to store x,y coordinates as strings
  let pathLength = 0; // Cumulative distance along the path

  // Iterate through angles, direction determined by sign of step
  for (
    let theta = startTheta;
    step > 0 ? theta <= endTheta : theta >= endTheta; // Handle both forward and reverse iteration
    theta += step
  ) {
    // Apply limaçon formula: r = a + b*cos(theta)
    // This creates the distinctive teardrop/loop shape
    let r = a + b * Math.cos(theta);

    // Convert polar coordinates (r, theta) to Cartesian (x, y)
    // x = centerX + r*cos(theta)
    // y = centerY + r*sin(theta)
    let x = centerX + r * Math.cos(theta);
    let y = centerY + r * Math.sin(theta);

    // Calculate distance from previous point for path length
    // Uses Pythagorean theorem: distance = sqrt(dx² + dy²)
    if (points.length > 0) {
      const prevPoint = points[points.length - 1].split(',');
      const dx = x - parseFloat(prevPoint[0]);
      const dy = y - parseFloat(prevPoint[1]);
      pathLength += Math.sqrt(dx * dx + dy * dy);
    }

    // Store point as "x,y" string
    points.push(`${x},${y}`);
  }

  // Return SVG path data (M = move to first point, L = line to subsequent points)
  // and the total calculated path length (needed for stroke-dasharray animation)
  return {
    pathData: 'M' + points.join(' L'),
    pathLength: pathLength,
  };
}

/**
 * ============================================================================
 * CHANGE LOOP - BOTTOM LIMAÇON
 * ============================================================================
 * Draws the bottom half of the "Change Loop" figure skating pattern.
 *
 * Position: Center at (-36, 250) creates a loop extending downward
 * Direction: Clockwise from 0 to 2π (full rotation)
 * Animation: Will animate from start to end using stroke-dashoffset
 *
 * @returns {{pathData: string, pathLength: number}} Path data and length
 */
export function drawChangeLoopLimaconBottom() {
  const centerX = -36; // Positioned left of center for the pattern layout
  const centerY = 250; // Vertical center of the SVG
  return generateLimaconPath(
    centerX,
    centerY,
    0, // Start at 0 radians (rightmost point)
    2 * Math.PI, // End at 2π radians (complete circle)
    0.01 // Small step for smooth curve (628 points)
  );
}

/**
 * ============================================================================
 * CHANGE LOOP - TOP LIMAÇON
 * ============================================================================
 * Draws the top half of the "Change Loop" figure skating pattern.
 *
 * Position: Center at (140, 160) creates a loop extending upward
 * Direction: Counter-clockwise from 2π to 0 (reversed for animation effect)
 * Animation: Reversed direction creates opposite drawing motion
 *
 * @returns {{pathData: string, pathLength: number}} Path data and length
 */
export function drawChangeLoopLimaconTop() {
  const centerX = 140; // Horizontal center of the SVG
  const centerY = 160; // Positioned above center
  return generateLimaconPath(
    centerX,
    centerY,
    2 * Math.PI, // Start at 2π (rightmost point)
    0, // End at 0 (complete circle in reverse)
    -0.01 // Negative step for counter-clockwise direction
  );
}

/**
 * ============================================================================
 * PARAGRAPH LOOP - PRIMARY LIMAÇON
 * ============================================================================
 * Draws the main limaçon for the "Paragraph Loop" figure.
 * This curve is specially positioned so its path starts at the exact center
 * of the SVG (140, 250), creating a symmetric figure-8 pattern.
 *
 * Mathematical positioning:
 * - At theta=0, the curve starts at x = centerX + (a+b)
 * - To start at x=140: centerX = 140 - (a+b) = 140 - 126 = 14
 *
 * Position: Center at (14, 250) so path begins at (140, 250)
 * Direction: Clockwise from 0 to 2π
 * Usage: Rotated ±90° in the SVG for final orientation
 *
 * @returns {{pathData: string, pathLength: number}} Path data and length
 */
export function drawParagraphLimacon() {
  // Position center so path starts at (140, 250) - the SVG center
  // At theta=0: x = centerX + (a+b) = 14 + 126 = 140
  const centerX = 140 - (a + b); // = 14
  const centerY = 250; // Vertical center
  return generateLimaconPath(
    centerX,
    centerY,
    0, // Start at 0 radians (center point after rotation)
    2 * Math.PI, // Complete rotation
    0.01 // Smooth curve resolution
  );
}

/**
 * ============================================================================
 * PARAGRAPH LOOP - SECONDARY LIMAÇON (UNUSED)
 * ============================================================================
 * Alternative limaçon for Paragraph Loop - currently not used in the final render.
 * Instead, the primary limacon is duplicated and rotated 180° for symmetry.
 *
 * This function is kept for reference and potential future use.
 *
 * Position: Center at (140, 180) with tangent at (140, 250)
 * Direction: Counter-clockwise starting from π/2 (bottom of curve)
 *
 * @returns {{pathData: string, pathLength: number}} Path data and length
 */
export function drawParagraphLimacon2() {
  const centerX = 140;
  const centerY = 180; // Positioned above center
  return generateLimaconPath(
    centerX,
    centerY,
    Math.PI / 2, // Start at bottom (90°)
    -(3 * Math.PI) / 2, // End at -270° (equivalent to 90° - full rotation)
    -0.01 // Counter-clockwise
  );
}
